<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

export type ProjectTab = 'project' | 'users';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();

const tabs: Array<{ value: ProjectTab; label: string }> = [
  { value: 'project', label: t('project') },
  { value: 'users', label: t('users') },
];

const selectedTab = computed<ProjectTab>(() => {
  return route.query.tab === 'anvandare' ? 'users' : 'project';
});

const tabToQueryValue: Record<ProjectTab, 'projekt' | 'anvandare'> = {
  project: 'projekt',
  users: 'anvandare',
};

const setTab = (tab: ProjectTab) => {
  void router.replace({
    query: {
      ...route.query,
      tab: tabToQueryValue[tab],
    },
  });
};
</script>

<template>
  <div
    class="tabs is-toggle is-toggle-rounded is-small project-tab-selector"
    role="tablist"
    aria-label="Project section selector"
  >
    <ul>
      <li
        v-for="tab in tabs"
        :key="tab.value"
        :class="{ 'is-active': selectedTab === tab.value }"
        role="presentation"
      >
        <button
          type="button"
          role="tab"
          class="tab-button"
          :aria-selected="selectedTab === tab.value"
          @click="setTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.project-tab-selector {
  margin-bottom: 1rem;

  ul {
    border-bottom: 0;
    gap: 0.5rem;
  }

  li {
    margin: 0;
  }

  .tab-button {
    border-radius: 999px;
    border: 1px solid var(--primary, #485fc7);
    background: transparent;
    color: var(--primary, #485fc7);
    font-weight: 600;
    min-width: 6.5rem;
    padding: 0.45rem 1rem;
    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      border-color 0.2s ease;
  }

  li.is-active .tab-button {
    background-color: var(--primary, #485fc7);
    color: var(--white, #ffffff);
    border-color: var(--primary, #485fc7);
  }

  .tab-button:focus-visible {
    outline: 2px solid rgba(72, 95, 199, 0.35);
    outline-offset: 2px;
  }
}
</style>
